import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { putGroup, postGroupResetSettlement } from "../services/AnimalSplitCore";
import { toggleModal } from "../utils/utils";
import { MODAL_ID } from "../constants";
import SelectLanguage from "../components/SelectLanguage";
import { useTranslation } from "react-i18next";
import AnimalEasterEggBadge from "../components/AnimalEasterEggBadge";
import ConfirmModal from "../components/ConfirmModal";
import "./ModalTheme.css";

export default function EditGroup(props) {
  const { t, i18n } = useTranslation();
  const {
    modalId,
    groupId,
    renderGroup,
    groupName,
    settlementType,
    setIsLoading,
    isSettled = false,
  } = props;
  const [newGroupName, setNewGroupName] = useState("");
  const [formValidated, setFormValidated] = useState(false);
  const [newLanguage, setNewLanguage] = useState("");
  const [showMoreActions, setShowMoreActions] = useState(false);
  
  const resetConfirmModalId = `${modalId}-reset-confirm`;

  const handleSave = async () => {
    if (!isFormValid()) {
      setFormValidated(true);
      return;
    }

    try {
      setIsLoading(true);
      const langToSend =
        newLanguage && newLanguage.startsWith("en") ? "en-US" : newLanguage;
      const { payload } = await putGroup(groupId, {
        name: newGroupName,
        settlementType: settlementType,
        lang: langToSend,
      });
      setIsLoading(false);
      renderGroup(payload);
      // `renderGroup` will call `i18n.changeLanguage` using the server-returned lang
      // so avoid changing language here to prevent mismatches.
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      toggleModal(MODAL_ID.CONNECTION_ERROR);
    }
  };

  useEffect(() => {
    // Init bootstrap tooltip component
    var tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.map(function (tooltipTriggerEl) {
      return new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  useEffect(() => {
    setNewGroupName(groupName);
    setNewLanguage(i18n.language);
  }, [groupName, i18n.language]);

  const handleModalWillShow = useCallback(() => {
    setFormValidated(false);
    setNewLanguage(i18n.language);

    // Reset group name
    const groupNameFloatingInput = document.getElementById(
      `${modalId}-floatingInput`
    );
    groupNameFloatingInput.value = groupName;
  }, [modalId, groupName, setFormValidated, i18n.language]);

  useEffect(() => {
    handleModalWillShow();
    const editGroupModal = document.getElementById(modalId);
    editGroupModal.addEventListener("show.bs.modal", handleModalWillShow);

    return () => {
      editGroupModal.removeEventListener("show.bs.modal", handleModalWillShow);
    };
  }, [modalId, handleModalWillShow]);

  const isFormValid = () => {
    return newGroupName !== "";
  };

  const showResetConfirm = useCallback(() => {
    const confirmEl = document.getElementById(resetConfirmModalId);
    if (!confirmEl || !window?.bootstrap?.Modal) return;
    try {
      const inst =
        window.bootstrap.Modal.getInstance(confirmEl) ||
        new window.bootstrap.Modal(confirmEl);
      inst.show();
    } catch (e) {
      // ignore
    }
  }, [resetConfirmModalId]);

  const handleResetSettlement = useCallback(async () => {
    if (!groupId || !isSettled) return;

    try {
      setIsLoading(true);
      const { payload } = await postGroupResetSettlement(groupId);
      setIsLoading(false);
      renderGroup(payload);
      
      // Close the confirm modal
      const confirmEl = document.getElementById(resetConfirmModalId);
      if (confirmEl && window?.bootstrap?.Modal) {
        const confirmInst = window.bootstrap.Modal.getInstance(confirmEl);
        if (confirmInst) confirmInst.hide();
      }
      
      // Close the edit group modal
      const modalEl = document.getElementById(modalId);
      if (modalEl && window?.bootstrap?.Modal) {
        const inst =
          window.bootstrap.Modal.getInstance(modalEl) ||
          new window.bootstrap.Modal(modalEl);
        inst.hide();
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      toggleModal(MODAL_ID.CONNECTION_ERROR);
    }
  }, [groupId, isSettled, setIsLoading, renderGroup, modalId, resetConfirmModalId]);

  const resetConfirmModal = (
    <ConfirmModal
      modalId={resetConfirmModalId}
      title={t("setting.reset_settlement_confirm_title", {
        defaultValue: "Reset settlement?",
      })}
      body={t("setting.reset_settlement_confirm_body", {
        defaultValue:
          "This will un-settle the group and allow you to add/edit contributors and transactions again. All settlement payment statuses will be lost.",
      })}
      icon="⚠️"
      confirmText={t("setting.reset_settlement_button", { defaultValue: "Reset settlement" })}
      cancelText={t("general.cancel", { defaultValue: "Cancel" })}
      confirmVariantClassName="btn-danger"
      onConfirm={handleResetSettlement}
    />
  );

  return (
    <>
    <div className="modal fade" id={modalId} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content as-modal as-theme">
          <div className="modal-header">
            <div className="as-modal-header-row">
              <div className="as-modal-title-row">
                <span className="as-modal-icon-badge" aria-hidden="true">
                  ⚙️
                </span>
                <h5 className="modal-title" id="exampleModalLabel">
                  {t("setting.title")}
                </h5>
              </div>
              <div className="as-modal-actions">
                <AnimalEasterEggBadge seed={`${groupId}:settings-modal`} />
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                ></button>
              </div>
            </div>
          </div>

          <div className={`modal-body ${formValidated ? "was-validated" : ""}`}>
            <div className="form-floating mb-3">
              <input
                type="text"
                className="form-control"
                id={`${modalId}-floatingInput`}
                placeholder="Trip to Mammoth Lake"
                onChange={(e) => setNewGroupName(e.target.value)}
                defaultValue={groupName}
                required
              />
              <label>{t("event.title")}</label>
            </div>
            <div>
              <label className="form-label d-block mt-3">
                {t("setting.language")}
              </label>
              <SelectLanguage
                className="d-block"
                type={newLanguage}
                setType={setNewLanguage}
              />
            </div>

            {isSettled ? (
              <div className="mt-4">
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0">
                    {t("setting.more_actions")}
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowMoreActions(!showMoreActions)}
                    aria-label={showMoreActions ? "Hide more actions" : "Show more actions"}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "10px",
                      border: "1px solid rgba(0, 0, 0, 0.10)",
                      background: "rgba(255, 255, 255, 0.35)",
                      color: "rgba(0, 0, 0, 0.60)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      verticalAlign: "middle",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <i className={`bi ${showMoreActions ? "bi-chevron-up" : "bi-chevron-down"}`} style={{ fontSize: "0.875rem" }} />
                  </button>
                </div>
                {showMoreActions ? (
                  <div 
                    className="mt-3 p-3 border rounded" 
                    style={{ 
                      background: "rgba(255, 0, 0, 0.04)",
                      borderColor: "rgba(220, 53, 69, 0.2)",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <span className="me-2" style={{ fontSize: "1.125rem" }}>⚠️</span>
                      <strong className="text-danger">
                        {t("setting.reset_settlement")}
                      </strong>
                    </div>
                    <div className="small text-muted mb-3" style={{ lineHeight: "1.5" }}>
                      {t("setting.reset_settlement_description")}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={showResetConfirm}
                      style={{
                        borderRadius: "8px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {t("setting.reset_settlement_button")}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="modal-footer as-modal-footer">
            <button
              type="button"
              className="btn as-btn-soft"
              data-bs-dismiss="modal"
            >
              {t("general.close")}
            </button>
            <button
              type="button"
              className="btn btn-primary as-btn-primary-gradient"
              onClick={handleSave}
              data-bs-dismiss={`${isFormValid() ? "modal" : ""}`}
            >
              {t("general.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
    {typeof document !== "undefined"
      ? createPortal(resetConfirmModal, document.body)
      : resetConfirmModal}
    </>
  );
}
